// Pure data-mapping module — no React, no charting library import. Kept separate from
// widget-explain-report.tsx (the UI renderer) and report-chart-block.tsx (the lazy-loaded
// chart renderer) so callers that only need to build a message payload
// (assistant-runtime-provider.tsx, widget-explain-bridge.tsx) don't transitively pull in either.
import type { CustomResponseData } from '@/lib/types'
import type { ParsedReport, ParsedReportBlock } from '@/service/research.api'
import { type ChartTimeframe, exchangeToJSON } from '@/types/generated/common/types'
import {
  type ChartIntegrity,
  type ChartReference,
  chartTypeToJSON,
} from '@/types/generated/research'

export interface WidgetExplainReportCitation {
  marker: string
  tool: string
  sourceTable: string
}

export interface WidgetExplainReportChartPoint {
  tMs: string
  o?: string
  h?: string
  l?: string
  c?: string
  v?: string
}

export interface WidgetExplainReportChartSeries {
  label: string
  points: WidgetExplainReportChartPoint[]
}

export interface WidgetExplainReportChartReference {
  feed: string
  symbol: string
  exchange: string
  intervalSeconds: ChartTimeframe
  fromTs: string
  toTs: string
  live: boolean
}

export interface WidgetExplainReportChartIntegrity {
  rows: number
  first?: string
  last?: string
  checksum?: string
}

export type WidgetExplainReportBlock =
  | { kind: 'markdown'; section: string; content: string }
  | {
      kind: 'chart'
      section: string
      caption: string
      chartType: string
      series: WidgetExplainReportChartSeries[]
      isPreview: boolean
      truncated: boolean
      reference?: WidgetExplainReportChartReference
      integrity?: WidgetExplainReportChartIntegrity
      // Open key set (map<string,string>) — read tolerantly, never switch UI state on it.
      aggregates: Record<string, string>
      sources: string[]
    }

export interface WidgetExplainReportData {
  blocks: WidgetExplainReportBlock[]
  citations: WidgetExplainReportCitation[]
  // Rendered verbatim with the report — server-authored compliance copy, never
  // reworded/parsed. See ReportMeta.disclaimers in research.proto.
  disclaimers: string[]
  // This report's OWN job_id (ReportMeta.job_id) — the "parent" a follow-up turn attaches to
  // (POST /research/{parent_job_id}/followup-research). Not report.meta.parentJobId, which
  // would only be set if THIS report were itself already a follow-up.
  jobId: string
  // "Ask a question" / "Refresh with latest data" only work from a chat in research mode (see
  // research-followup-actions.tsx) — the sparkle-triggered widget-explain flow never switches
  // chatMode to 'research', so arming a follow-up target there gets silently cleared by
  // order-x-composer.tsx's own mode guard. False for that flow; true for general research chat.
  showFollowupActions: boolean
}

function toChartReference(
  reference: ChartReference | undefined
): WidgetExplainReportChartReference | undefined {
  if (!reference) return undefined
  return {
    feed: reference.feed,
    symbol: reference.symbol,
    exchange: exchangeToJSON(reference.exchange),
    intervalSeconds: reference.intervalSeconds,
    fromTs: reference.fromTs,
    toTs: reference.toTs,
    live: reference.live,
  }
}

function toChartIntegrity(
  integrity: ChartIntegrity | undefined
): WidgetExplainReportChartIntegrity | undefined {
  if (!integrity) return undefined
  return {
    rows: integrity.rows,
    first: integrity.first,
    last: integrity.last,
    checksum: integrity.checksum,
  }
}

function toReportBlock(block: ParsedReportBlock): WidgetExplainReportBlock {
  if (block.kind === 'markdown') {
    return { kind: 'markdown', section: block.section, content: block.content }
  }
  return {
    kind: 'chart',
    section: block.section,
    caption: block.caption,
    chartType: chartTypeToJSON(block.chartType),
    series: block.series.map((series) => ({
      label: series.label,
      points: series.points.map((point) => ({
        tMs: point.tMs,
        o: point.o,
        h: point.h,
        l: point.l,
        c: point.c,
        v: point.v,
      })),
    })),
    isPreview: block.isPreview,
    truncated: block.truncated,
    reference: toChartReference(block.reference),
    integrity: toChartIntegrity(block.integrity),
    aggregates: block.aggregates,
    sources: block.sources,
  }
}

// Cast (not just typed) as CustomResponseData: that type's plain `Record<string, unknown>`
// index signature can't structurally match a concrete interface without one, same as every
// other custom response card's data payload in this codebase.
export function toWidgetExplainReportData(
  report: ParsedReport,
  options: { showFollowupActions: boolean }
): CustomResponseData {
  const data: WidgetExplainReportData = {
    blocks: report.blocks.map(toReportBlock),
    citations: report.citations.map((citation) => ({
      marker: citation.marker,
      tool: citation.tool,
      sourceTable: citation.sourceTable,
    })),
    disclaimers: report.meta.disclaimers,
    jobId: report.meta.jobId,
    showFollowupActions: options.showFollowupActions,
  }
  return data as unknown as CustomResponseData
}
